import { hasValidSession } from "./authTokenUtils";
import { isMobileWebClient } from "./deviceUtils";
import { shouldForceHealthInterestsScreen } from "./healthInterestsStorage";
import { shouldForceTermsAcceptance } from "./termsAndConditionsStorage";

export const TERMS_ACCEPTED_EVENT = "mytara:terms-accepted";
export const COIN_SUMMARY_STAGED_EVENT = "mytara:coin-summary-staged";
export const COIN_POPUP_STARTED_EVENT = "mytara:coin-popup-started";
export const COIN_POPUP_FINISHED_EVENT = "mytara:coin-popup-finished";
export const HEALTH_INTERESTS_GATE_EVENT = "mytara:health-interests-gate-changed";
export const COIN_POPUP_DURATION_MS = 3300;

let coinPopupActive = false;

export const ONBOARDING_PUBLIC_PATHS = new Set([
  "/",
  "/login",
  "/register",
  "/verify-otp",
  "/home",
  "/legal/terms",
]);

export function isCoinGateBlocking(): boolean {
  if (globalThis.window === undefined) return false;
  if (!hasValidSession()) {
    coinPopupActive = false;
    return false;
  }
  return hasPendingCoinSummary() || coinPopupActive;
}

export function resetCoinPopupGate(): void {
  coinPopupActive = false;
}

export function isOnboardingPublicPath(pathname: string): boolean {
  return ONBOARDING_PUBLIC_PATHS.has(pathname);
}

/** Web-only: user must accept T&C before app content loads. */
export function isTermsGateBlocking(pathname: string): boolean {
  if (!hasValidSession()) return false;
  if (isMobileWebClient()) return false;
  if (isOnboardingPublicPath(pathname)) return false;
  return shouldForceTermsAcceptance();
}

export function notifyTermsAccepted(): void {
  if (globalThis.window === undefined) return;
  globalThis.dispatchEvent(new CustomEvent(TERMS_ACCEPTED_EVENT));
}

export function notifyCoinSummaryStaged(): void {
  if (globalThis.window === undefined) return;
  globalThis.dispatchEvent(new CustomEvent(COIN_SUMMARY_STAGED_EVENT));
}

export function hasPendingCoinSummary(): boolean {
  if (globalThis.window === undefined) return false;
  try {
    const stored =
      localStorage.getItem("coinSummary") ||
      sessionStorage.getItem("coinSummary");
    if (!stored) return false;
    const parsed = JSON.parse(stored) as { earned?: number; total?: number };
    return Boolean(parsed?.earned || parsed?.total);
  } catch {
    return false;
  }
}

/** Web login: play coin popup only after T&C gate clears. */
export function shouldDeferCoinsForTerms(): boolean {
  if (globalThis.window === undefined) return false;
  if (isMobileWebClient()) return false;
  if (!hasValidSession()) return false;
  return shouldForceTermsAcceptance();
}

/** True while terms, coin popup, or health interests still block the dashboard. */
export function isDashboardOnboardingBlocking(pathname: string): boolean {
  if (!hasValidSession()) return false;
  if (isMobileWebClient()) return false;
  if (isOnboardingPublicPath(pathname)) return false;
  if (isTermsGateBlocking(pathname)) return true;
  if (isCoinGateBlocking()) return true;
  if (shouldForceHealthInterestsScreen()) return true;
  return false;
}

export function notifyCoinPopupStarted(): void {
  if (globalThis.window === undefined) return;
  coinPopupActive = true;
  globalThis.dispatchEvent(new CustomEvent(COIN_POPUP_STARTED_EVENT));
}

export function notifyCoinPopupFinished(): void {
  if (globalThis.window === undefined) return;
  coinPopupActive = false;
  globalThis.dispatchEvent(new CustomEvent(COIN_POPUP_FINISHED_EVENT));
}

export function notifyHealthInterestsGateChanged(): void {
  if (globalThis.window === undefined) return;
  globalThis.dispatchEvent(new CustomEvent(HEALTH_INTERESTS_GATE_EVENT));
}
